import type { MonthView, TransactionView } from '../api.js'
import {
  addMoney,
  cents,
  esc,
  fromCents,
  isPositive,
  money,
  money2,
  monthLong,
  monthName,
  shortDate,
} from '../format.js'

/**
 * Bills, payroll, income sources, the budget table and the transaction log.
 *
 * Markup carried over from the prototype. Classification — which bucket a
 * charge falls in, whether an Amazon purchase is still missing details — is
 * decided by the server and arrives on each transaction.
 */

const $ = <T extends HTMLElement>(selector: string): T =>
  document.querySelector<T>(selector) as T

const sampleTag = (isSample: boolean) =>
  isSample ? '<span class="tag ex" style="margin-left:6px">sample</span>' : ''

const editButton = (target: string, label: string, canEdit: boolean) =>
  canEdit
    ? `<button class="icon-btn" data-edit="${target}" aria-label="Edit ${esc(label)}">✎</button>`
    : ''

export function renderBills(view: MonthView, canEdit: boolean): void {
  const bills = view.bills
  const paidCount = bills.filter((bill) => bill.paid).length
  const unpaidTotal = addMoney(...bills.filter((bill) => !bill.paid).map((bill) => bill.amount))
  const categoryName = (id: string | null) =>
    view.categories.find((category) => category.id === id)?.name ?? 'No category'

  $('#billsMeta').innerHTML = bills.length
    ? `${paidCount} of ${bills.length} paid · <span class="num">${money(unpaidTotal)}</span> left`
    : ''

  $('#bills').innerHTML = bills.length
    ? bills
        .map(
          (bill) => `<li class="row bill${bill.paid ? ' done' : ''}">
      <input type="checkbox" class="chk" data-paid="${bill.id}" ${bill.paid ? 'checked' : ''} ${
        canEdit ? '' : 'disabled'
      } aria-label="Mark ${esc(bill.name)} paid">
      <div class="name">${esc(bill.name)}<div class="note">${
        bill.autopay ? 'Autopay · ' : ''
      }${esc(categoryName(bill.categoryId))}</div></div>
      <span class="pillcell"><span class="pill ${bill.status.kind}">${esc(bill.status.label)}</span></span>
      <span class="amt num">${money2(bill.amount)}</span>
      ${editButton(`bills:${bill.id}`, bill.name, canEdit)}</li>`,
        )
        .join('')
    : `<li class="empty">No bills yet. Add software, insurance, phones, vehicle payments and anything else due each month.</li>`
}

export function renderPayroll(view: MonthView, canEdit: boolean): void {
  const people = view.people
  const shortMonth = monthLong(view.month).split(' ')[0]?.slice(0, 3) ?? ''

  $('#payMeta').innerHTML = people.length
    ? `<span class="num">${money(view.summary.payrollScheduled)}</span> this month`
    : ''

  $('#payroll').innerHTML = people.length
    ? people
        .map(
          (person) => `<li class="row pay">
      <div class="name">${esc(person.name)}
        <div class="note">${esc(person.kind)} · ${money2(person.amount)} ${esc(
          person.frequency.toLowerCase(),
        )}</div>
        <div class="dates">${
          person.paydays
            .map((date) => `<span class="num">${shortMonth} ${Number(date.slice(8, 10))}</span>`)
            .join('') || '<span>No paydays this month</span>'
        }</div></div>
      <span class="amt num">${money2(person.monthTotal)}</span>
      ${editButton(`payroll:${person.id}`, person.name, canEdit)}</li>`,
        )
        .join('')
    : `<li class="empty">Add each person you pay, including owner pay, with how often they're paid.</li>`
}

export function renderIncome(view: MonthView, canEdit: boolean): void {
  const receivedBySource = new Map<string, string>()
  for (const transaction of view.transactions) {
    if (transaction.kind !== 'income') continue
    const key = transaction.incomeSourceId ?? ''
    receivedBySource.set(key, addMoney(receivedBySource.get(key) ?? '0.00', transaction.amount))
  }

  const { revenue } = view.summary
  $('#incMeta').innerHTML =
    `Expected <span class="num">${money(view.expectedRecurringTotal)}</span> · ` +
    `Received <span class="num">${money(revenue.recurringReceived)}</span>` +
    (isPositive(revenue.additionalReceived)
      ? ` · Additional <span class="num">${money(revenue.additionalReceived)}</span>`
      : '')

  const label = $('#l-rrm')
  if (label) label.textContent = `Recurring Monthly Revenue · ${monthLong(view.month)}`

  const input = $<HTMLInputElement>('#p-rrm')
  if (input && input !== document.activeElement) {
    const ownValue = view.rmr && !view.rmr.inherited ? view.rmr.amount : ''
    input.value = ownValue
    input.placeholder = view.rmr ? view.rmr.amount : '0'
    input.disabled = !canEdit
  }

  // Offer the sources' total when it differs from what the month holds. It is
  // applied on a click rather than automatically: each month keeps its own
  // figure, so a source changed in March must not rewrite what October said.
  const suggestion = $('#rrmSuggest')
  if (suggestion) {
    const total = view.expectedRecurringTotal
    const current = view.rmr?.amount ?? null
    const differs = current === null || cents(total) !== cents(current)
    suggestion.hidden = !canEdit || !isPositive(total) || !differs
    suggestion.innerHTML = differs
      ? `<button class="btn small" id="useExpected" data-total="${total}">Use ${money(
          total,
        )} — the total of your recurring sources</button>`
      : ''
  }

  $('#rrmNote').innerHTML =
    (view.rmr === null
      ? '<b style="color:var(--warn)">Not set yet.</b> Enter what NI reliably makes in a normal month.'
      : view.rmr.inherited
        ? `Carried forward from ${esc(monthName(view.rmr.fromMonth))}. Type a new amount to change it from ${esc(
            monthName(view.month),
          )} on.`
        : `Set for ${esc(monthName(view.month))}.`) +
    ' Additional revenue (one-time projects, installs, equipment, special work) is tracked ' +
    'separately and never raises this number. Weekly card charges are planned from this amount.'

  const other = receivedBySource.get('') ?? '0.00'

  $('#income').innerHTML =
    view.incomeSources
      .map(
        (source) => `<li class="row inc">
      <div class="name">${esc(source.name)}<div class="note"><span class="tag rt-${
        source.revenueType === 'Recurring' ? 'Recurring' : 'Free'
      }">${source.revenueType}</span><span class="tag" style="background:var(--panel-2);color:var(--muted)">usually ${esc(
        source.defaultPaymentMethod,
      )}</span></div></div>
      <span class="amt"><span class="num">${money2(source.expectedMonthly)}</span><div class="note">expected</div>
        <span class="num recvd">${money2(receivedBySource.get(source.id) ?? '0.00')}</span><div class="note">received</div></span>
      ${editButton(`income:${source.id}`, source.name, canEdit)}</li>`,
      )
      .join('') +
    (isPositive(other)
      ? `<li class="row inc"><div class="name">Other business income<div class="note"><span class="tag rt-Free">Additional</span></div></div><span class="amt"><span class="num">${money2(
          other,
        )}</span><div class="note">received</div></span><span></span></li>`
      : '') +
    (view.incomeSources.length === 0 && !isPositive(other)
      ? `<li class="empty">Add where revenue comes from, such as the weekly card charges (recurring) and projects or installs (additional).</li>`
      : '')
}

export function renderBudget(view: MonthView, canEdit: boolean): void {
  const rows = view.summary.categories
  const spentInCategories = addMoney(...rows.map((row) => row.spent))

  $('#catMeta').innerHTML = rows.length
    ? `<span class="num">${money(spentInCategories)}</span> of <span class="num">${money(
        view.summary.budgeted,
      )}</span>`
    : ''

  const row = (
    name: string,
    budget: string,
    spent: string,
    state: 'ok' | 'warn' | 'over',
    extraClass: string,
    edit: string,
    withBar: boolean,
  ) => {
    const remaining = cents(budget) - cents(spent)
    const percent = cents(budget) > 0 ? Math.min(100, (cents(spent) / cents(budget)) * 100) : spentBar(spent)
    const remainingText =
      remaining < 0
        ? `${money(fromCents(-remaining))} over`
        : money(fromCents(remaining))

    return `<li class="row cat${extraClass}"><div class="name">${name}</div>
      <span class="nums"><span class="c"><span class="lbl-m">Budget</span><span class="num">${money(budget)}</span></span>
      <span class="c"><span class="lbl-m">Spent</span><span class="num">${money(spent)}</span></span>
      <span class="c rem ${state === 'over' ? 'over' : state === 'warn' ? 'close' : 'ok'}"><span class="lbl-m">Remaining</span><span class="num">${remainingText}</span></span></span>
      <span class="edit-c">${edit}</span>${
        withBar
          ? `<div class="bar"><i class="${
              state === 'over' ? 'bad' : state === 'warn' ? 'warn' : ''
            }" style="width:${percent}%"></i></div>`
          : ''
      }</li>`
  }

  const totalState: 'ok' | 'warn' | 'over' =
    cents(view.summary.budgetRemaining) < 0 ? 'over' : 'ok'

  $('#cats').innerHTML = rows.length
    ? rows
        .map((category) =>
          row(
            esc(category.name) +
              (category.isProcessingFees
                ? '<span class="tag" style="margin-left:6px">fees</span>'
                : ''),
            category.budget,
            category.spent,
            category.state,
            '',
            editButton(`categories:${category.categoryId}`, category.name, canEdit),
            true,
          ),
        )
        .join('') +
      row('<b>Total</b>', view.summary.budgeted, spentInCategories, totalState, ' cattotal', '', false) +
      (isPositive(view.summary.uncategorised)
        ? `<li class="row"><div class="note">Plus <b class="num">${money2(
            view.summary.uncategorised,
          )}</b> spent with no category yet (see Investigate).</div></li>`
        : '')
    : `<li class="empty">Add categories with a monthly amount for each, like Software, Equipment or Vehicle &amp; fuel.</li>`
}

function spentBar(spent: string): number {
  return cents(spent) > 0 ? 100 : 0
}

const BUCKET_LABELS: Array<[TransactionView['bucket'], string]> = [
  ['Bills', 'Bills'],
  ['RS', 'RS'],
  ['Investigate', '? Investigate'],
]

export function renderLog(view: MonthView, canEdit: boolean, filter: string): void {
  const all = [...view.transactions].sort((a, b) => b.occurredOn.localeCompare(a.occurredOn))
  const expenses = all.filter((transaction) => transaction.kind === 'expense')
  const inBucket = (bucket: string) =>
    expenses.filter((transaction) => transaction.bucket === bucket)
  const total = (list: TransactionView[]) => addMoney(...list.map((item) => item.amount))

  const filters: Array<[string, string, string | null]> = [
    ['All', 'All', null],
    ['Bills', 'Bills', total(inBucket('Bills'))],
    ['RS', 'RS', total(inBucket('RS'))],
    ['Investigate', '? Investigate', total(inBucket('Investigate'))],
    ['Deposits', 'Income', total(all.filter((item) => item.kind === 'income'))],
  ]
  if (all.some((item) => item.kind === 'transfer')) {
    filters.push(['Transfers', 'Transfers', total(all.filter((item) => item.kind === 'transfer'))])
  }

  $('#filters').innerHTML = filters
    .map(
      ([value, label, sum]) =>
        `<button type="button" class="chip" data-filter="${value}" aria-pressed="${
          filter === value
        }">${label}${sum !== null ? `<b>${money(sum)}</b>` : ''}</button>`,
    )
    .join('')

  const shown =
    filter === 'All'
      ? all
      : filter === 'Deposits'
        ? all.filter((item) => item.kind === 'income')
        : filter === 'Transfers'
          ? all.filter((item) => item.kind === 'transfer')
          : inBucket(filter)

  $('#logMeta').textContent = all.length
    ? filter === 'All'
      ? `${all.length} this month`
      : `${shown.length} of ${all.length}`
    : ''

  const sourceName = (id: string | null) =>
    view.incomeSources.find((source) => source.id === id)?.name ?? 'Other business income'
  const categoryName = (id: string | null) =>
    view.categories.find((category) => category.id === id)?.name ?? 'No category'

  const bucketSelect = (transaction: TransactionView) =>
    `<select class="bucket b-${transaction.bucket}" data-bucket="${transaction.id}" aria-label="Spending bucket" ${
      canEdit && !transaction.bucketLocked ? '' : 'disabled'
    } ${
      transaction.bucketLocked
        ? 'title="Stays in Investigate until the Amazon purchase is documented"'
        : ''
    }>${BUCKET_LABELS.map(
      ([value, label]) =>
        `<option value="${value}" ${value === transaction.bucket ? 'selected' : ''}>${label}</option>`,
    ).join('')}</select>`

  $('#log').innerHTML = shown.length
    ? shown
        .map((transaction) => {
          const isIncome = transaction.kind === 'income'
          const isTransfer = transaction.kind === 'transfer'
          const label = isTransfer
            ? 'Transfer between NI accounts · not income'
            : isIncome
              ? `${transaction.revenueType === 'Recurring' ? 'Recurring revenue' : 'Additional revenue'} · ${
                  transaction.paymentMethod ?? 'Other'
                } · ${sourceName(transaction.incomeSourceId)}`
              : categoryName(transaction.categoryId)

          return `<li class="row log"><span class="d num">${shortDate(transaction.occurredOn)}</span>
      <div class="name">${esc(transaction.description || label)}${sampleTag(transaction.isSample)}<div class="note">${esc(
        label,
      )}${transaction.report?.item ? ` · ${esc(transaction.report.item)}` : ''}${
        transaction.report ? ` · reported · ${esc(transaction.report.status)}` : ''
      }</div>${
        transaction.amazon
          ? `<div class="amz">⚠️ Amazon purchase needs details: ${esc(
              transaction.amazon.missing.join(', '),
            )}${
              canEdit
                ? ` <button class="btn small" data-amazon="${transaction.id}">Add details</button>`
                : ''
            }</div>`
          : ''
      }</div>
      <span class="bk">${isIncome || isTransfer ? '' : bucketSelect(transaction)}</span>
      <span class="amt num ${isIncome ? 'in' : ''}" ${
        isTransfer ? 'style="color:var(--muted)"' : ''
      }>${isIncome ? '+' : isTransfer ? '⇄ ' : ''}${money2(transaction.amount)}</span>
      ${
        canEdit
          ? `<button class="icon-btn edit" data-edit="entries:${transaction.id}" aria-label="Edit entry">✎</button>`
          : '<span></span>'
      }</li>`
        })
        .join('')
    : `<li class="empty">${
        filter === 'Investigate'
          ? 'Nothing to investigate this month.'
          : 'Nothing here for this month yet.'
      }</li>`
}
